import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { MillieAvatar } from "../components/MascotBubble";

export default function Register() {
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created — let's set up your household!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F3EDE6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-background px-6 pt-16 pb-10 flex flex-col">
        <div className="flex flex-col items-center text-center gap-3">
          <MillieAvatar size={80} />
          <h1 className="font-heading text-3xl font-bold text-sorted-ink">Let's get you sorted</h1>
          <p className="text-sorted-slate text-base max-w-xs">Create an account and I'll plan dinners your whole household will actually eat.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="relative">
            <User size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="register-name" required placeholder="Your name" value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white border-2 border-[#FFF3F0] rounded-2xl pl-12 pr-4 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-peach transition-colors" />
          </div>
          <div className="relative">
            <Mail size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="register-email" type="email" required placeholder="you@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border-2 border-[#FFF3F0] rounded-2xl pl-12 pr-4 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-peach transition-colors" />
          </div>
          <div className="relative">
            <Lock size={18} strokeWidth={2.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-sorted-slate" />
            <input data-testid="register-password" type={showPw ? "text" : "password"} required minLength={6} placeholder="Password (6+ chars)" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border-2 border-[#FFF3F0] rounded-2xl pl-12 pr-12 py-4 text-sorted-ink font-medium outline-none focus:border-sorted-peach transition-colors" />
            <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="register-toggle-password" aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-sorted-slate hover:text-sorted-ink">
              {showPw ? <EyeOff size={18} strokeWidth={2.5} /> : <Eye size={18} strokeWidth={2.5} />}
            </button>
          </div>
          <button data-testid="register-submit" disabled={loading}
            className="w-full bg-sorted-peach hover:bg-[#FCA696] text-sorted-ink font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
            {!loading && <ArrowRight size={18} strokeWidth={2.5} />}
          </button>
        </form>

        <p className="mt-auto text-center text-sorted-slate text-sm pt-8">
          Already have an account?{" "}
          <Link to="/login" data-testid="go-login" className="font-bold text-sorted-ink underline underline-offset-2">Sign in</Link>
        </p>
        <p className="mt-2 text-center text-[11px] text-sorted-slate font-semibold">By creating an account you agree to our <Link to="/terms" data-testid="register-terms" className="underline underline-offset-2">Terms</Link> and <Link to="/privacy" data-testid="register-privacy" className="underline underline-offset-2">Privacy policy</Link>.</p>
      </div>
    </div>
  );
}
